<template>
  <div class="tw-max-w-md tw-mx-auto">
    <div class="tw-bg-white tw-shadow-lg tw-rounded-lg tw-p-8">
      <!-- Header -->
      <div class="tw-text-center tw-mb-8">
        <div
            class="tw-mx-auto tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-100 tw-mb-4"
>
          <svg
              class="tw-h-6 tw-w-6 tw-text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
          >
            <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
        <h2 class="tw-text-2xl tw-font-bold tw-text-gray-900">
          Approve Your Quote
        </h2>
        <p class="tw-mt-2 tw-text-sm tw-text-gray-600">
          Enter your job number, postcode and PIN code to proceed with payment
        </p>
      </div>

      <!-- Form -->
      <form
          class="tw-space-y-6"
          @submit.prevent="handleSubmit"
      >
        <!-- Quote Reference Input -->
        <div>
          <label
              for="quoteReference"
              class="tw-block tw-text-sm tw-font-medium tw-text-gray-700"
          >
            Job Number
          </label>
          <div class="tw-mt-1 tw-relative">
            <input
                id="quoteReference"
                v-model="formData.quoteReference"
                type="text"
                required
                placeholder="e.g., QS-2025-001234"
                :class="[
                'tw-block tw-w-full tw-rounded-md tw-shadow-sm',
                'tw-px-3 tw-py-2 tw-pr-10',
                'tw-border-gray-300 focus:tw-border-blue-500 focus:tw-ring-blue-500',
                'tw-placeholder-gray-400',
                errors.quoteReference ? 'tw-border-red-300' : ''
              ]"
                :disabled="loading"
                @input="clearError('quoteReference')"
            >
            <div
                v-if="errors.quoteReference"
                class="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-3"
            >
              <svg
                  class="tw-h-5 tw-w-5 tw-text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
              >
                <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
          <p
              v-if="errors.quoteReference"
              class="tw-mt-2 tw-text-sm tw-text-red-600"
          >
            {{ errors.quoteReference }}
          </p>
        </div>

        <!-- Postcode Input -->
        <div>
          <label
              for="postcode"
              class="tw-block tw-text-sm tw-font-medium tw-text-gray-700"
          >
            Billing Postcode
          </label>
          <div class="tw-mt-1 tw-relative">
            <input
                id="postcode"
                v-model="formData.postcode"
                type="text"
                required
                placeholder="e.g., SW1A 1AA"
                :class="[
                'tw-block tw-w-full tw-rounded-md tw-shadow-sm',
                'tw-px-3 tw-py-2 tw-pr-10',
                'tw-border-gray-300 focus:tw-border-blue-500 focus:tw-ring-blue-500',
                'tw-placeholder-gray-400',
                errors.postcode ? 'tw-border-red-300' : ''
              ]"
                :disabled="loading"
                @input="handlePostcodeInput"
            >
            <div
                v-if="errors.postcode"
                class="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-3"
            >
              <svg
                  class="tw-h-5 tw-w-5 tw-text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
              >
                <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
          <p
              v-if="errors.postcode"
              class="tw-mt-2 tw-text-sm tw-text-red-600"
          >
            {{ errors.postcode }}
          </p>
        </div>

        <!-- PIN Code Input -->
        <div>
          <label
              for="pinCode"
              class="tw-block tw-text-sm tw-font-medium tw-text-gray-700"
          >
            PIN Code
          </label>
          <div class="tw-mt-1 tw-relative">
            <input
                id="pinCode"
                v-model="formData.pinCode"
                type="text"
                required
                placeholder="Unique PIN from your quote email"
                :class="[
                'tw-block tw-w-full tw-rounded-md tw-shadow-sm',
                'tw-px-3 tw-py-2 tw-pr-10',
                'tw-border-gray-300 focus:tw-border-blue-500 focus:tw-ring-blue-500',
                'tw-placeholder-gray-400',
                errors.pinCode ? 'tw-border-red-300' : ''
              ]"
                :disabled="loading"
                @input="clearError('pinCode')"
            >
            <div
                v-if="errors.pinCode"
                class="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-3"
            >
              <svg
                  class="tw-h-5 tw-w-5 tw-text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
              >
                <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
          <p
              v-if="errors.pinCode"
              class="tw-mt-2 tw-text-sm tw-text-red-600"
          >
            {{ errors.pinCode }}
          </p>
        </div>

        <!-- General Error Message -->
        <div
            v-if="generalError"
            class="tw-rounded-md tw-bg-red-50 tw-p-4"
        >
          <div class="tw-flex">
            <div class="tw-flex-shrink-0">
              <svg
                  class="tw-h-5 tw-w-5 tw-text-red-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
              >
                <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="tw-ml-3">
              <p class="tw-text-sm tw-text-red-800">
                {{ generalError }}
              </p>
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <button
            type="submit"
            :disabled="loading || !isFormValid"
            :class="[
            'tw-w-full tw-flex tw-justify-center tw-items-center',
            'tw-px-4 tw-py-2 tw-border tw-border-transparent',
            'tw-rounded-md tw-shadow-sm tw-text-sm tw-font-medium tw-text-white',
            loading || !isFormValid 
              ? 'tw-bg-gray-400 tw-cursor-not-allowed' 
              : 'tw-bg-blue-600 hover:tw-bg-blue-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-blue-500'
          ]"
        >
          <svg
              v-if="loading"
              class="tw-animate-spin tw--ml-1 tw-mr-3 tw-h-5 tw-w-5 tw-text-white"
              fill="none"
              viewBox="0 0 24 24"
          >
            <circle
                class="tw-opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
            />
            <path
                class="tw-opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          {{ loading ? 'Looking up quote...' : 'Continue to Payment' }}
        </button>
      </form>

      <!-- Help Text -->
      <div class="tw-mt-6 tw-text-center">
        <p class="tw-text-sm tw-text-gray-500">
          Can't find your quote reference?
        </p>
        <a
            href="#"
            class="tw-text-sm tw-font-medium tw-text-blue-600 hover:tw-text-blue-500"
            @click.prevent="showHelp"
        >
          Check your email for quote details
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import {ref, computed, inject} from 'vue'

export default {
  name: 'ManualLookup',
  emits: ['lookup-success', 'lookup-error'],
  setup(props, {emit}) {
    const quoteBuilderService = inject('quoteBuilderService')

    // Form data
    const formData = ref({
      quoteReference: '',
      postcode: '',
      pinCode: ''
    })

    // Error states
    const errors = ref({
      quoteReference: '',
      postcode: '',
      pinCode: ''
    })
    const generalError = ref('')

    // Loading state
    const loading = ref(false)

    // Computed property for form validation
    const isFormValid = computed(() => {
      return formData.value.quoteReference.trim() !== '' &&
          formData.value.postcode.trim() !== '' &&
          formData.value.pinCode.trim() !== '' &&
          !errors.value.quoteReference &&
          !errors.value.postcode &&
          !errors.value.pinCode
    })

    // Clear specific error
    const clearError = (field) => {
      errors.value[field] = ''
      generalError.value = ''
    }

    // Handle postcode input with formatting
    const handlePostcodeInput = (event) => {
      let value = event.target.value.toUpperCase()
      formData.value.postcode = value
      clearError('postcode')

      // Basic UK postcode validation
      if (value.length > 3 && !validatePostcode(value)) {
        errors.value.postcode = 'Please enter a valid UK postcode'
      }
    }

    // Validate UK postcode format
    const validatePostcode = (postcode) => {
      const postcodeRegex = /^[A-Z]{1,2}[0-9]{1,2}[A-Z]?\s?[0-9][A-Z]{2}$/i
      return postcodeRegex.test(postcode.trim())
    }

    // Handle form submission
    const handleSubmit = async () => {
      // Reset errors
      generalError.value = ''
      Object.keys(errors.value).forEach(key => errors.value[key] = '')

      // Validate form
      if (!formData.value.quoteReference.trim()) {
        errors.value.quoteReference = 'Quote reference is required'
        return
      }

      if (!formData.value.postcode.trim()) {
        errors.value.postcode = 'Postcode is required'
        return
      }

      if (!formData.value.pinCode.trim()) {
        errors.value.pinCode = 'PIN code is required'
        return
      }

      if (!validatePostcode(formData.value.postcode)) {
        errors.value.postcode = 'Please enter a valid UK postcode'
        return
      }

      loading.value = true

      try {
        // Call the service to lookup the quote
        const response = await quoteBuilderService.getQuoteByReference({
          reference: formData.value.quoteReference.trim(),
          postcode: formData.value.postcode.trim().replace(/\s/g, ''), // Remove spaces from postcode
          pin: formData.value.pinCode.trim()
        })

        if (response && response.data) {
          // Success - emit the quote data
          emit('lookup-success', response.data)
        } else {
          throw new Error('No quote found')
        }
      } catch (error) {
        console.error('Quote lookup failed:', error)

        if (error.response?.status === 404) {
          generalError.value = 'No quote found with the provided reference and postcode. Please check your details and try again.'
        } else if (error.response?.status === 400) {
          generalError.value = error.response.data?.message || 'Invalid quote details provided.'
        } else {
          generalError.value = 'Unable to lookup quote. Please try again later or contact support.'
        }

        emit('lookup-error', generalError.value)
      } finally {
        loading.value = false
      }
    }

    // Show help modal/info
    const showHelp = () => {
      alert('Your quote reference was sent to your email address when the quote was created. It typically starts with a few letters followed by numbers. If you cannot find it, please contact our support team.')
    }

    return {
      formData,
      errors,
      generalError,
      loading,
      isFormValid,
      clearError,
      handlePostcodeInput,
      handleSubmit,
      showHelp
    }
  }
}
</script>

<style scoped>
/* Custom styles if needed */
</style>
